import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FaTimes, FaCheck } from "react-icons/fa";
import { Link } from "react-router-dom";

const DRAFT_KEY = "fundinglab_application_draft_v2";

// Sends the same event to both Google Analytics (gtag.js) and Microsoft
// Clarity so form funnel performance (opens, step drop-off, validation
// errors, submissions) can be measured. Both scripts load globally from
// public/index.html; guarded since ad blockers commonly stub them out.
const trackEvent = (name, params = {}) => {
  try {
    window.gtag?.("event", name, { form_name: "funding_lab_apply", ...params });
  } catch {}
  try {
    window.clarity?.("event", name);
  } catch {}
};

// Fixed session times, given in Pacific Time — kept in sync manually with
// the mirrored schedule in server/index.js and public/api/funding-lab-apply.php.
const SESSION_SLOTS = [
  { id: "sep4-0800", date: "Fri, Sept 4", hour24: 8 },
  { id: "sep4-1600", date: "Fri, Sept 4", hour24: 16 },
  { id: "sep8-0800", date: "Tue, Sept 8", hour24: 8 },
  { id: "sep11-0800", date: "Fri, Sept 11", hour24: 8 },
  { id: "sep11-1600", date: "Fri, Sept 11", hour24: 16 },
  { id: "sep15-0800", date: "Tue, Sept 15", hour24: 8 },
];

// Canadian time zones as hour offsets from Pacific Time during September
// (all daylight time, so this stays correct until DST ends in November).
const TIMEZONES = [
  { label: "Pacific Time (PT)", abbrev: "PT", offset: 0 },
  { label: "Mountain Time (MT)", abbrev: "MT", offset: 1 },
  { label: "Central Time (CT)", abbrev: "CT", offset: 2 },
  { label: "Eastern Time (ET)", abbrev: "ET", offset: 3 },
  { label: "Atlantic Time (AT)", abbrev: "AT", offset: 4 },
  { label: "Newfoundland Time (NT)", abbrev: "NT", offset: 4.5 },
];

const formatSessionTime = (hour24, offsetHours) => {
  const totalMinutes = (((hour24 * 60 + offsetHours * 60) % 1440) + 1440) % 1440;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const sessionLabelFor = (slotId, timezoneLabel) => {
  const slot = SESSION_SLOTS.find((s) => s.id === slotId);
  if (!slot) return "";
  const tz = TIMEZONES.find((t) => t.label === timezoneLabel) || TIMEZONES[0];
  return `${slot.date} — ${formatSessionTime(slot.hour24, tz.offset)} ${tz.abbrev}`;
};

const STEPS = [
  {
    title: "About You",
    fields: [
      { name: "founder_name", label: "Founder's full name", type: "text", required: true },
      { name: "work_email", label: "Work email", type: "email", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true, placeholder: "+1 (___) ___-____" },
      { name: "linkedin_url", label: "LinkedIn profile", type: "text", required: false, placeholder: "https://linkedin.com/in/…" },
      { name: "company_name", label: "Company name", type: "text", required: true },
      { name: "company_website", label: "Company website", type: "text", required: false, placeholder: "https://…" },
      { name: "city_country", label: "City and country", type: "text", required: true },
      {
        name: "company_stage", label: "Company stage", type: "select", required: true,
        options: ["Prototype", "Product launched", "Pre-revenue", "Early revenue", "Growth", "Research or deep technology"],
      },
    ],
  },
  {
    title: "Business and Capital",
    fields: [
      {
        name: "revenue_range", label: "Revenue during the previous 12 months", type: "select", required: true,
        options: ["Pre-revenue", "Under CAD $50,000", "CAD $50,000–$249,999", "CAD $250,000–$999,999", "CAD $1 million–$4.9 million", "CAD $5 million+"],
      },
      {
        name: "capital_range", label: "How much capital do you need during the next 12 months?", type: "select", required: true,
        options: ["Under CAD $100,000", "CAD $100,000–$249,999", "CAD $250,000–$499,999", "CAD $500,000–$999,999", "CAD $1 million–$2.9 million", "CAD $3 million+"],
      },
    ],
  },
  {
    title: "Funding Fit",
    fields: [
      {
        name: "funding_types", label: "Which types of funding do you want to pursue?", type: "multiselect", required: true,
        options: ["Government grants", "R&D tax credits and incentives", "Competitions or corporate programs", "Customer financing or paid pilots", "Angel investment", "Venture capital", "Strategic investment", "Loans or lines of credit", "Revenue-based financing", "SAFEs or convertible notes", "Unsure — I need help choosing"],
      },
      {
        name: "grant_areas", label: "Which grant and funding areas apply to your company?", type: "multiselect", required: false,
        options: ["Innovation and R&D", "Artificial intelligence and digital technology", "Sustainability and clean technology", "Health and life sciences", "Agriculture and food", "Manufacturing and industrial technology", "Social impact and community development", "Jobs, skills and workforce development", "Export and international expansion", "Inclusive entrepreneurship", "Indigenous innovation", "Creative industries", "Infrastructure and smart communities", "Aerospace, defence or ocean technology", "None or unsure"],
      },
      {
        name: "funding_challenge", label: "What is your greatest funding challenge?", type: "select", required: true,
        options: ["Finding suitable grants", "Preparing grant applications", "Building a capital strategy", "Understanding funding runway", "Creating investor materials", "Finding suitable investors", "Securing customer financing", "Evaluating debt", "Combining multiple funding sources", "Previous applications or raises have failed", "Other"],
      },
      { name: "hear_about", label: "How did you hear about Funding Lab?", type: "text", required: true },
    ],
  },
  {
    title: "Capital Readiness Session",
    fields: [
      {
        name: "capital_readiness_timezone", label: "Your time zone", type: "select", required: true,
        prompt: "Session times below convert automatically to your time zone.",
        options: TIMEZONES.map((t) => t.label),
      },
      {
        name: "capital_readiness_session", label: "Attend Capital Readiness Session", type: "session-select", required: true,
      },
    ],
  },
];

const emptyFormData = () => {
  const data = {};
  STEPS.forEach((step) =>
    step.fields.forEach((f) => {
      data[f.name] = f.type === "multiselect" ? [] : "";
    })
  );
  return data;
};

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const isFieldFilled = (field, value) => {
  if (!field.required) return true;
  if (field.type === "multiselect") return Array.isArray(value) && value.length > 0;
  return typeof value === "string" && value.trim() !== "";
};

const FundingLabApplyModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(emptyFormData);
  const [confirmAccuracy, setConfirmAccuracy] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [optInUpdates, setOptInUpdates] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Hydrate the saved draft once, when the modal is first opened.
  useEffect(() => {
    if (!open) return;
    const draft = loadDraft();
    if (draft) {
      setFormData((prev) => ({ ...prev, ...draft.formData }));
      setStep(draft.step || 0);
      setConfirmAccuracy(!!draft.confirmAccuracy);
      setConfirmTerms(!!draft.confirmTerms);
      setOptInUpdates(!!draft.optInUpdates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Autosave on every change while the modal is open and not yet submitted.
  useEffect(() => {
    if (!open || submitted) return;
    const draft = { formData, step, confirmAccuracy, confirmTerms, optInUpdates };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [open, submitted, formData, step, confirmAccuracy, confirmTerms, optInUpdates]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Funnel tracking: modal open, and the step currently in view (fires again
  // whenever step changes, including back navigation).
  useEffect(() => {
    if (!open) return;
    trackEvent("funding_lab_apply_open");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    trackEvent("funding_lab_apply_step_view", {
      step_number: step + 1,
      step_name: STEPS[step].title,
    });
  }, [open, step]);

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  const totalRequired = useMemo(
    () => STEPS.reduce((sum, s) => sum + s.fields.filter((f) => f.required).length, 0),
    []
  );

  const setValue = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const toggleMultiValue = (name, option) => {
    setFormData((prev) => {
      const current = prev[name] || [];
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [name]: next };
    });
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateStep = () => {
    const stepErrors = {};
    currentStep.fields.forEach((f) => {
      if (!isFieldFilled(f, formData[f.name])) {
        stepErrors[f.name] = "This field is required.";
      }
    });
    setErrors(stepErrors);
    const invalidFields = Object.keys(stepErrors);
    if (invalidFields.length > 0) {
      trackEvent("funding_lab_apply_validation_error", {
        step_number: step + 1,
        step_name: currentStep.title,
        invalid_fields: invalidFields.join(","),
      });
    }
    return invalidFields.length === 0;
  };

  const goNext = () => {
    if (!validateStep()) return;
    trackEvent("funding_lab_apply_step_complete", {
      step_number: step + 1,
      step_name: currentStep.title,
    });
    if (isLastStep) return;
    setStep((s) => s + 1);
    document.querySelector(".fl2-modal-body")?.scrollTo({ top: 0 });
  };

  const goBack = () => {
    setStep((s) => Math.max(0, s - 1));
    document.querySelector(".fl2-modal-body")?.scrollTo({ top: 0 });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    if (!confirmAccuracy || !confirmTerms) {
      trackEvent("funding_lab_apply_confirm_error");
      toast.error("Please confirm both required checkboxes before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/funding-lab-apply.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          confirm_accuracy: confirmAccuracy,
          confirm_terms: confirmTerms,
          opt_in_updates: optInUpdates,
        }),
      });

      if (!response.ok) throw new Error("Request failed");

      trackEvent("funding_lab_apply_submit", {
        company_stage: formData.company_stage,
        capital_range: formData.capital_range,
      });

      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
    } catch (err) {
      trackEvent("funding_lab_apply_submit_error", { message: String(err?.message || err) });
      toast.error("Couldn't submit your application — please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitted) {
      trackEvent("funding_lab_apply_close", {
        step_number: step + 1,
        step_name: currentStep.title,
      });
    }
    setErrors({});
    onClose();
  };

  const resetAndClose = () => {
    localStorage.removeItem(DRAFT_KEY);
    setFormData(emptyFormData());
    setStep(0);
    setConfirmAccuracy(false);
    setConfirmTerms(false);
    setOptInUpdates(false);
    setSubmitted(false);
    onClose();
  };

  if (!open) return null;

  const progressPct = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="fl2-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="fl2-modal" role="dialog" aria-modal="true" aria-label="Funding Lab application">
        <button type="button" className="fl2-modal-close" onClick={handleClose} aria-label="Close">
          <FaTimes />
        </button>

        {submitted ? (
          <div className="fl2-modal-success">
            <div className="fl2-modal-success-badge"><FaCheck /></div>
            <h3>Application received</h3>
            <p>
              Thanks — we've got your Funding Lab application. We'll review it and follow up by email
              at {formData.work_email || "the address you provided"} if you're shortlisted.
            </p>
            <button type="button" className="fl2-btn fl2-btn-solid" onClick={resetAndClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="fl2-modal-header">
              <span className="fl2-modal-step-label">
                Step {step + 1} of {STEPS.length} — {currentStep.title}
              </span>
              <div className="fl2-modal-progress-track">
                <div className="fl2-modal-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
              {step === 0 && (
                <p className="fl2-modal-intro">
                  Takes approximately 8–10 minutes. Your progress is saved automatically
                  ({totalRequired} required fields across {STEPS.length} short steps).
                </p>
              )}
            </div>

            <div className="fl2-modal-body">
              {currentStep.fields.map((field) => (
                <div className="fl2-field" key={field.name}>
                  <label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="fl2-field-required">*</span>}
                  </label>
                  {field.prompt && <p className="fl2-field-prompt">{field.prompt}</p>}

                  {(field.type === "text" || field.type === "email" || field.type === "tel") && (
                    <input
                      id={field.name}
                      type={field.type}
                      value={formData[field.name]}
                      placeholder={field.placeholder}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      className={errors[field.name] ? "fl2-input-error" : ""}
                    />
                  )}

                  {field.type === "number" && (
                    <input
                      id={field.name}
                      type="number"
                      min="0"
                      value={formData[field.name]}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      className={errors[field.name] ? "fl2-input-error" : ""}
                    />
                  )}

                  {field.type === "textarea" && (
                    <textarea
                      id={field.name}
                      rows={3}
                      value={formData[field.name]}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      className={errors[field.name] ? "fl2-input-error" : ""}
                    />
                  )}

                  {field.type === "select" && (
                    <div className="fl2-pill-group">
                      {field.options.map((opt) => (
                        <button
                          type="button"
                          key={opt}
                          className={`fl2-pill ${formData[field.name] === opt ? "is-selected" : ""}`}
                          onClick={() => setValue(field.name, opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {field.type === "multiselect" && (
                    <div className="fl2-pill-group">
                      {field.options.map((opt) => (
                        <button
                          type="button"
                          key={opt}
                          className={`fl2-pill ${(formData[field.name] || []).includes(opt) ? "is-selected" : ""}`}
                          onClick={() => toggleMultiValue(field.name, opt)}
                        >
                          {(formData[field.name] || []).includes(opt) && <FaCheck className="fl2-pill-check" />}
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {field.type === "session-select" && (
                    <div className="fl2-pill-group">
                      {SESSION_SLOTS.map((slot) => (
                        <button
                          type="button"
                          key={slot.id}
                          className={`fl2-pill ${formData[field.name] === slot.id ? "is-selected" : ""}`}
                          onClick={() => setValue(field.name, slot.id)}
                        >
                          {sessionLabelFor(slot.id, formData.capital_readiness_timezone)}
                        </button>
                      ))}
                    </div>
                  )}

                  {errors[field.name] && <p className="fl2-field-error">{errors[field.name]}</p>}
                </div>
              ))}

              {isLastStep && (
                <div className="fl2-modal-confirms">
                  <label className="fl2-checkbox-row">
                    <input
                      type="checkbox"
                      checked={confirmAccuracy}
                      onChange={(e) => setConfirmAccuracy(e.target.checked)}
                    />
                    <span>
                      I confirm that this information is accurate and that I am authorized to apply on
                      behalf of the company. <span className="fl2-field-required">*</span>
                    </span>
                  </label>
                  <label className="fl2-checkbox-row">
                    <input
                      type="checkbox"
                      checked={confirmTerms}
                      onChange={(e) => setConfirmTerms(e.target.checked)}
                    />
                    <span>
                      I accept the <Link to="/terms" target="_blank" rel="noreferrer">privacy policy</Link> and understand that
                      participation does not guarantee grants, investment, debt approval or introductions.{" "}
                      <span className="fl2-field-required">*</span>
                    </span>
                  </label>
                  <label className="fl2-checkbox-row">
                    <input
                      type="checkbox"
                      checked={optInUpdates}
                      onChange={(e) => setOptInUpdates(e.target.checked)}
                    />
                    <span>Send me funding opportunities, application deadlines and future program announcements.</span>
                  </label>
                </div>
              )}
            </div>

            <div className="fl2-modal-footer">
              <button
                type="button"
                className="fl2-btn fl2-btn-text"
                onClick={goBack}
                disabled={step === 0}
                style={{ visibility: step === 0 ? "hidden" : "visible" }}
              >
                ← Back
              </button>
              {isLastStep ? (
                <button
                  type="button"
                  className="fl2-btn fl2-btn-solid"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting…" : "Submit Application"}
                </button>
              ) : (
                <button type="button" className="fl2-btn fl2-btn-solid" onClick={goNext}>
                  Next →
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FundingLabApplyModal;
